"use client";

import { useCallback, useEffect, useState, type RefObject } from "react";

import Link from "next/link";
import { useRouter } from "next/navigation";

import { ChevronDown, ChevronRight } from "lucide-react";

import { BrandCard } from "@/components/chat/BrandCard";
import { ChatSearchResultsPreview } from "@/components/chat/ChatSearchResultsPreview";
import { MessageAvatar } from "@/components/chat/MessageAvatar";
import { SearchChip } from "@/components/chat/SearchChip";
import { ThinkingIndicator } from "@/components/chat/ThinkingIndicator";
import { ThinkingText } from "@/components/shared/ThinkingText";
import { TraceLink } from "@/components/ui/TraceLink";
import { useAuth } from "@/contexts/AuthContext";
import { useChat } from "@/contexts/ChatContext";
import { useDebug } from "@/contexts/DebugContext";
import { useCurrentSearchId } from "@/hooks/useCurrentSearchId";
import { buildCanonicalBrandPath } from "@/lib/brand-routing";
import { type ChatMessage } from "@/lib/chat-message-parser";
import { formatTimestampDivider, hasSignificantTimeGap } from "@/lib/date-utils";
import { isInternalUser } from "@/lib/internal-user";
import { chatSpacing, colors, typography } from "@/styles/design-tokens";

export interface ChatMessagesProps {
  messages: ChatMessage[];
  chatId: string;
  containerRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  showHistoryLink?: boolean;
  showHistoryLinkOnEmpty?: boolean;
  historyLinkHref?: string;
  emptyStateFooter?: React.ReactNode;
  searchResultsDisplay?: "chips" | "inline";
}

export function ChatMessages({
  messages,
  chatId,
  containerRef,
  className = "px-3 py-2 sm:px-4 sm:py-3",
  showHistoryLink = false,
  showHistoryLinkOnEmpty = false,
  historyLinkHref = "/chats",
  emptyStateFooter,
  searchResultsDisplay = "chips",
}: ChatMessagesProps) {
  const router = useRouter();
  const currentSearchId = useCurrentSearchId();
  const { isDebug } = useDebug();
  const { user, loading: authLoading } = useAuth();
  const { setSelectedSearchId } = useChat();
  const canViewTraceLinks = isDebug && !authLoading && isInternalUser(user?.email);
  const [expandedBrands, setExpandedBrands] = useState<Set<number>>(new Set());

  useEffect(() => {
    setExpandedBrands(new Set());
  }, [chatId]);

  const toggleBrandExpansion = (index: number) => {
    setExpandedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const scrollToBottom = useCallback(() => {
    if (containerRef?.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [containerRef]);

  return (
    <div
      ref={containerRef}
      className={`scrollbar-thin-y flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden ${className}`}
    >
      <div className="mt-auto space-y-3 sm:space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <p className="text-sm" style={{ color: colors.text.grayLightest }}>
              Start a conversation...
            </p>
            {(showHistoryLink || showHistoryLinkOnEmpty) && (
              <Link
                href={historyLinkHref}
                className="text-xs hover:underline"
                style={{ color: colors.text.accentMdGray }}
              >
                View all chat history →
              </Link>
            )}
            {emptyStateFooter && <div className="mt-3 w-full">{emptyStateFooter}</div>}
          </div>
        )}

        {showHistoryLink && messages.length > 0 && (
          <div className="flex justify-center">
            <Link
              href={historyLinkHref}
              className="text-xs hover:underline"
              style={{ color: colors.text.accentMdGray }}
            >
              View all chat history →
            </Link>
          </div>
        )}

        {messages.map((message, index) => {
          const isUser = message.role === "user";
          const messageKey = message.timestamp
            ? `${message.role}-${message.timestamp}`
            : `${message.role}-${index}`;
          const isThinking = message.isStreaming && !message.content;
          const hasInlineSearchResults =
            !isUser &&
            searchResultsDisplay === "inline" &&
            Boolean(message.searchTools && message.searchTools.length > 0);
          const messageWidthClass = hasInlineSearchResults
            ? "max-w-full sm:max-w-[92%]"
            : "max-w-[80%] sm:max-w-[75%]";

          // Check if we should show a timestamp divider (5+ min gap from previous message)
          const prevMessage = index > 0 ? messages[index - 1] : null;
          const showTimestampDivider =
            index === 0 || hasSignificantTimeGap(prevMessage?.timestamp, message.timestamp, 5);

          return (
            <div key={messageKey}>
              {/* Centered timestamp divider */}
              {showTimestampDivider && message.timestamp && !message.isStreaming && (
                <div className="flex justify-center py-2">
                  <span
                    className="text-[10px] uppercase tracking-wide"
                    style={{ color: colors.text.grayLightest }}
                  >
                    {formatTimestampDivider(message.timestamp)}
                  </span>
                </div>
              )}
              <div className={`flex items-start gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
                {!isUser && (
                  <div className={isThinking ? "mt-[3px]" : "mt-[7px]"}>
                    {isThinking ? <ThinkingIndicator /> : <MessageAvatar isUser={false} />}
                  </div>
                )}

                <div
                  className={`flex flex-col ${isUser ? "items-end" : "items-start"} ${messageWidthClass}`}
                >
                  {isThinking && !isUser ? (
                    <div className="py-0.5">
                      <ThinkingText
                        activeColor={colors.text.black}
                        inactiveColor={colors.text.grayLightest}
                        ellipsisStyle={{
                          fontSize: typography.fontSizes["16"],
                          lineHeight: typography.lineHeights.normal,
                        }}
                        className="text-[13px] leading-snug sm:text-sm"
                        onLineChange={scrollToBottom}
                      />
                    </div>
                  ) : (
                    <div
                      className={`rounded-2xl px-3 py-2 text-[13px] leading-snug sm:text-sm ${isUser ? "text-white" : "text-black"}`}
                      style={{
                        backgroundColor: isUser
                          ? colors.stroke.pureBlack
                          : colors.accent.lightestGray,
                      }}
                    >
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                  )}

                  {!isUser &&
                    searchResultsDisplay === "chips" &&
                    message.searchTools &&
                    message.searchTools.length > 0 && (
                      <div className="mt-4 w-full">
                        <div
                          className="mb-2 px-1 text-xs font-medium"
                          style={{ color: colors.text.accentMdGray }}
                        >
                          View Results
                        </div>
                        <div className="flex flex-col gap-1.5">
                          {message.searchTools.map((searchTool, toolIndex) => {
                            const hasDirectMatch =
                              message.searchTools?.some((st) => st.searchId === currentSearchId) ??
                              false;
                            const isActive =
                              currentSearchId === searchTool.searchId ||
                              (!hasDirectMatch && toolIndex === 0 && currentSearchId === chatId);
                            const hasNavigableSearchId = Boolean(searchTool.searchId);
                            // Keep chips interactive as soon as we have a concrete search target,
                            // even if the assistant is still streaming narrative content.
                            const isChipLoading =
                              Boolean(message.isStreaming) && !hasNavigableSearchId;

                            return (
                              <SearchChip
                                key={searchTool.searchId ?? toolIndex}
                                searchTool={searchTool}
                                isLoading={isChipLoading}
                                isActive={isActive}
                                isFirstLoading={toolIndex === 0 && isChipLoading}
                                onClick={() => {
                                  if (!searchTool.searchId) return;
                                  // Track this as the selected search for follow-up refinements
                                  setSelectedSearchId(chatId, searchTool.searchId);
                                  router.push(`/results/${searchTool.searchId}`);
                                }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}

                  {!isUser &&
                    searchResultsDisplay === "inline" &&
                    message.searchTools &&
                    message.searchTools.length > 0 && (
                      <div className="mt-4 w-full space-y-3">
                        <div
                          className="px-1 text-xs font-medium"
                          style={{ color: colors.text.accentMdGray }}
                        >
                          Search previews
                        </div>
                        {message.searchTools.map((searchTool, toolIndex) => {
                          const hasSearchId = Boolean(searchTool.searchId);
                          return (
                            <ChatSearchResultsPreview
                              key={searchTool.searchId ?? toolIndex}
                              chatId={chatId}
                              searchTool={searchTool}
                              isLoading={Boolean(message.isStreaming) && !hasSearchId}
                              onViewFullResults={(searchId) => {
                                setSelectedSearchId(chatId, searchId);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}

                  {!isUser && message.brandResults && message.brandResults.length > 0 && (
                    <div className="mt-2 w-full sm:mt-3">
                      <button
                        type="button"
                        onClick={() => toggleBrandExpansion(index)}
                        className="flex w-full items-center justify-between rounded-lg py-2.5 transition-all hover:bg-gray-50"
                        style={{
                          paddingLeft: chatSpacing.chipPaddingX,
                          paddingRight: chatSpacing.chipPaddingX,
                          border: `1px solid ${colors.divider.default}`,
                          backgroundColor: colors.background.white,
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="text-sm font-medium"
                            style={{ color: colors.text.accentMdGray }}
                          >
                            Suggested Brands
                          </span>
                          <span
                            className="flex h-5 w-5 items-center justify-center rounded-full text-xs"
                            style={{
                              backgroundColor: colors.accent.lightestGray,
                              color: colors.text.grayLightest,
                            }}
                          >
                            {message.brandResults.length}
                          </span>
                        </div>
                        {expandedBrands.has(index) ? (
                          <ChevronDown
                            className="h-3 w-3"
                            style={{ color: colors.text.grayLightest }}
                          />
                        ) : (
                          <ChevronRight
                            className="h-3 w-3"
                            style={{ color: colors.text.grayLightest }}
                          />
                        )}
                      </button>

                      {expandedBrands.has(index) && (
                        <div className="mt-2 space-y-2">
                          {message.brandResults.map((brand, brandIndex) => (
                            <BrandCard
                              key={brandIndex}
                              brand={brand}
                              onClick={() =>
                                router.push(
                                  buildCanonicalBrandPath({
                                    brandName: brand.brand_name,
                                    brandSlug: brand.brand_slug,
                                  })
                                )
                              }
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Debug trace link (no timestamp - using grouped dividers now) */}
                  {!message.isStreaming && canViewTraceLinks && !isUser && (
                    <span className="mt-1 px-1">
                      <TraceLink
                        runId={message.runId}
                        threadId={chatId}
                        langsmithProject={message.langsmithProject}
                        langsmithWorkspaceId={message.langsmithWorkspaceId}
                        langsmithProjectId={message.langsmithProjectId}
                      />
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
